/**這個Adapter已經使用了autoThrowErrorFetch，不需要在使用時另外加上autoThrowError了 */
import { useContext, useEffect } from "react";
import { API_Version, ApiVersionInfo, ResultObj, autoThrowErrorFetch, getControllerApiVersion } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { normalizeDsId } from "../function";


// HintBellApi的Hooks使用方式
export const useHintBellApi = () => {
    const apiManagerName = "hintBellApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useHintBellApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newHintBellApiManager = new HintBellApiManager();
            newHintBellApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing API Manager", e);
            });
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<HintBellApiManager>(apiManagerName);
};


// HintBellApiInterface 的定義
export interface HintBellApiInterface {
    /** 新增通知 */
    HintBellLogCreate: (obj: HintBellLogCreateParams) => Promise<ResultObj>
    /** 查詢通知 */
    HintBellLogSearch: (obj: HintBellLogSearchParams) => Promise<ResultObj<HintBellLogSearchResItem>>
    /** 提示鈴鐺紀錄已讀 */
    HintBellLogRead: (obj: HintBellLogReadParams) => Promise<ResultObj>
    /** 查詢通知細項 */
    HintBellLogDetail: (obj: HintBellLogDetailParams) => Promise<ResultObj<HintBellLogDetailResItem>>
}

// HintBellApiManager 類別實現
export class HintBellApiManager implements HintBellApiInterface {

    private adapter: HintBellDotnet6Adapter | HintBellFrameworkAdapter | HintBellDotnet6AMNAdapter | null = null;
    private version: keyof typeof API_Version | undefined;

    constructor(version?: keyof typeof API_Version) {
        this.version = version;
    }

    async init(): Promise<HintBellApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("HintBell");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === "dotNet6") this.adapter = new HintBellDotnet6Adapter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new HintBellDotnet6AMNAdapter();
        else if (target_version === "framework") this.adapter = new HintBellFrameworkAdapter();

        if (this.adapter !== null) {
            this.HintBellLogCreate = this.adapter.HintBellLogCreate;
            this.HintBellLogSearch = this.adapter.HintBellLogSearch;
            this.HintBellLogRead = this.adapter.HintBellLogRead;
            this.HintBellLogDetail = this.adapter.HintBellLogDetail;

        }
        return this;
    }

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: 'API未實現[HintBellAdapter]', data: null });

    HintBellLogCreate = async (obj: HintBellLogCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    HintBellLogSearch = async (obj: HintBellLogSearchParams): Promise<ResultObj<HintBellLogSearchResItem>> => await this.emptyAdapter();
    HintBellLogRead = async (obj: HintBellLogReadParams): Promise<ResultObj> => await this.emptyAdapter();
    HintBellLogDetail = async (obj: HintBellLogDetailParams): Promise<ResultObj<HintBellLogDetailResItem>> => await this.emptyAdapter();
}

class HintBellBaseAdapter implements HintBellApiInterface {
    HintBellLogCreate = async (obj: HintBellLogCreateParams): Promise<ResultObj> => await autoThrowErrorFetch('HintBell/HintBellLogCreate', {
        ...obj,
        ul_ids: obj.ul_ids?.map(item => ({ ...item, ds_id: normalizeDsId(item.ds_id) })),
        ud_ids: obj.ud_ids?.map(item => ({ ...item, ds_id: normalizeDsId(item.ds_id) })),
        user_ids: obj.user_ids?.map(item => ({ ...item, ds_id: normalizeDsId(item.ds_id) })),
    });
    HintBellLogSearch = async (obj: HintBellLogSearchParams): Promise<ResultObj<HintBellLogSearchResItem>> => await autoThrowErrorFetch('HintBell/HintBellLogSearch', obj);
    HintBellLogRead = async (obj: HintBellLogReadParams): Promise<ResultObj> => await autoThrowErrorFetch('HintBell/HintBellLogRead', obj);
    HintBellLogDetail = async (obj: HintBellLogDetailParams): Promise<ResultObj<HintBellLogDetailResItem>> => await autoThrowErrorFetch('HintBell/HintBellLogDetail', obj);
}

class HintBellDotnet6Adapter extends HintBellBaseAdapter {
}

class HintBellDotnet6AMNAdapter extends HintBellDotnet6Adapter {
    HintBellLogSearch = async (obj: HintBellLogSearchParams): Promise<ResultObj<HintBellLogSearchResItem>> => await autoThrowErrorFetch('/EnvironmentDataAggregation/HintBellLogSearch', obj)
}
class HintBellFrameworkAdapter extends HintBellBaseAdapter {
    HintBellLogSearch = async (obj: HintBellLogSearchParams): Promise<ResultObj<HintBellLogSearchResItem>> => await autoThrowErrorFetch('HintBell/HintBellLogSearch', obj).then((res) => {
        return {
            ...res,
            data: {
                ...res.data,
                hintbells: res.data?.hintbells.map((item: FrameworkHintBellLogSearchResItemHintbell) => {
                    const { hbl_id, ...rest } = item;
                    return {
                        ...rest,
                        hbrl_id: hbl_id,
                    }
                })
            }
        }
    });
    HintBellLogRead = async (obj: HintBellLogReadParams): Promise<ResultObj> => {
        const { hbrl_id, ...rest } = obj;
        return await autoThrowErrorFetch('HintBell/HintBellLogRead', {
            ...rest,
            hbl_id: hbrl_id
        });
    }
    HintBellLogDetail = async (obj: HintBellLogDetailParams): Promise<ResultObj<HintBellLogDetailResItem>> => await autoThrowErrorFetch('HintBell/HintBellLogDetail', obj).then((res) => {
        const { hbl_id, ...rest } = res.data;
        return {
            ...res,
            data: {
                ...rest,
                hbrl_id: hbl_id,
            }
        }
    });

}
interface UlItem {
    ds_id: string; // 經銷商ID
    ul_ids: string[]; // 職務IDs
}
interface UdItem {
    ds_id: string; // 經銷商ID
    ud_ids: string[]; // 職責IDs
}
interface UserItem {
    ds_id: string; // 經銷商ID
    user_ids: string[]; // 帳號IDs
}

/**
 * @description 新增提示鈴鐺記錄(參數)
 * @param {UlItem[]} ul_ids 經銷商職務項目陣列
 * @param {UdItem[]} ud_ids 經銷商職責項目陣列
 * @param {UserItem[]} user_ids 經銷商帳號項目陣列
 * @param {string} sps_name 類型代碼名稱
 * @param {string} json 導頁後查詢需要的參數
 * @param {string} title 主題
 * @param {string} apply_name 申請人名稱
 * @param {string} status 狀態
 * @param {string} info_json 通知需要顯示的內容
 * @param {string} is_notice_only 是否僅為通知
 */
export interface HintBellLogCreateParams {
    ul_ids?: UlItem[];
    ud_ids?: UdItem[];
    user_ids?: UserItem[];
    sps_name: string;
    json: string;
    title?: string;
    apply_name?: string;
    status?: string;
    info_json?: string;
    is_notice_only?: string;
}


/**
 * @description 查詢鈴鐺記錄(參數)
 * @param {string} is_read 是否已讀
 * @param {string} status 狀態
 * @param {string} sps_code 代碼代號
 * @param {string} is_notice_only 是否僅為通知
 * @param {number} page 分頁頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface HintBellLogSearchParams {
    is_read?: string;
    status?: string;
    sps_code?: string;
    is_notice_only?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 提示鈴鐺記錄項目
 * @param {string} cre_time 建立時間
 * @param {number} hbrl_id 提示鈴鐺已讀紀錄ID
 * @param {string} user_id 被通知使用者ID
 * @param {string} is_read 是否已讀
 * @param {string} sps_id 類型代碼ID
 * @param {string} sps_name 類型代碼名稱
 * @param {string} sps_code 類型代碼編號
 * @param {string} sps_remark 類型代碼備註
 * @param {string} json 導頁後查詢需要的參數
 * @param {string} title 主題
 * @param {string} apply_name 申請人名稱
 * @param {string} status 狀態
 * @param {string} info_json 通知需要顯示的內容
 * @param {string} is_notice_only 是否僅為通知
 * @param {number} source_ds_id 來源經銷商ID
 */
export interface DotNet6HintBellLogSearchResItemHintbell {
    cre_time: string;
    hbrl_id: number;
    hbl_id: number;
    user_id: string;
    is_read: string;
    sps_id: string;
    sps_name: string;
    sps_code: string;
    sps_remark: string;
    json: string;
    title: string;
    apply_name: string;
    status: string;
    info_json: string;
    is_notice_only: string;
    source_ds_id: number;
}

export interface FrameworkHintBellLogSearchResItemHintbell {
    cre_time: string;
    hbl_id: number;
    user_id: string;
    is_read: string;
    sps_id: string;
    sps_name: string;
    sps_code: string;
    sps_remark: string;
    json: string;
    title: string;
    apply_name: string;
    status: string;
    info_json: string;
    is_notice_only: string;
}

//將以DotNet6版本為主
export type HintBellLogSearchResItemHintbell = DotNet6HintBellLogSearchResItemHintbell

/**
 * @description 查詢鈴鐺記錄(回傳)
 * @param {number} read_count 已讀數量
 * @param {number} unread_count 未讀數量
 * @param {HintBellLogSearchResItemHintbell[]} hintbells 鈴鐺記錄陣列
 */
export interface HintBellLogSearchResItem {
    read_count: number;
    unread_count: number;
    hintbells: HintBellLogSearchResItemHintbell[];
}

/**
 * @description 提示鈴鐺記錄已讀(參數)
 * @param {number} hbrl_id 提示鈴鐺已讀紀錄ID
 */
export interface HintBellLogReadParams {
    hbrl_id: number;
}

/**
 * @description 查詢鈴鐺記錄細項(參數)
 * @param {number} hbrl_id 提示鈴鐺已讀紀錄ID
 */
export interface HintBellLogDetailParams {
    hbrl_id: number;
}

/**
 * @description 查詢鈴鐺記錄細項(回傳)
 * @param {string} cre_time 建立時間
 * @param {number} hbrl_id 提示鈴鐺已讀紀錄ID
 * @param {string} user_id 被通知使用者ID
 * @param {string} is_read 是否已讀
 * @param {string} sps_id 類型代碼ID
 * @param {string} sps_name 類型代碼名稱
 * @param {string} sps_code 類型代碼編號
 * @param {string} sps_remark 類型代碼備註
 * @param {string} json 導頁後查詢需要的參數
 * @param {string} title 主題
 * @param {string} apply_name 申請人名稱
 * @param {string} status 狀態
 * @param {string} info_json 通知需要顯示的內容
 * @param {string} is_notice_only 是否僅為通知
 * @param {number} source_ds_id 來源經銷商ID
 */
export interface HintBellLogDetailResItem {
    cre_time: string;
    hbrl_id: number;
    user_id: string;
    is_read: string;
    sps_id: string;
    sps_name: string;
    sps_code: string;
    sps_remark: string;
    json: string;
    title: string;
    apply_name: string;
    status: string;
    info_json: string;
    is_notice_only: string;
    source_ds_id: number;
}